'use client';

import { motion } from 'framer-motion';
import { CatalogCard } from '../catalog-card';
import type { CatalogItem, CatalogType } from '@/lib/services/catalog/catalog-types';

interface StandardGridProps {
  artworks: CatalogItem[];
  columns: number;
  onView?: (artwork: CatalogItem) => void;
  onEnlarge: (artwork: CatalogItem) => void;
  onMouseEnter?: (artwork: CatalogItem) => void;
  onMouseLeave?: () => void;
  onMouseMove?: (e: React.MouseEvent) => void;
  onCopyWallet?: (walletAddress: string | undefined) => void;
  copiedWallet?: string | null;
  catalogType?: CatalogType;
  useAspectRatio?: boolean;
}

export function StandardGrid({
  artworks,
  columns = 4,
  onView,
  onEnlarge,
  onMouseEnter,
  onMouseLeave,
  onMouseMove,
  onCopyWallet,
  copiedWallet,
  catalogType = 'all',
  useAspectRatio = false,
}: StandardGridProps) {
  // Sütun sayısına göre grid sınıfını belirle
  const getGridClass = () => {
    const gridClass = 'grid-cols-1 sm:grid-cols-2';
    switch (columns) {
      case 8:
        return ` ${gridClass} md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8`;
      case 6:
        return ` ${gridClass} md:grid-cols-3 lg:grid-cols-6`;
      case 4:
      default:
        return ` ${gridClass} md:grid-cols-2 lg:grid-cols-4`;
    }
  };

  const series = artworks.filter(item => item.type === 'collection');
  const separateSeries = series.length > 0 && artworks.some(item => item.type === 'artwork');
  const groups = separateSeries
    ? [{ kind: 'series', items: series }, { kind: 'artworks', items: artworks.filter(item => item.type !== 'collection') }]
    : [{ kind: undefined, items: artworks }];

  return (
    <div className="standard-grid space-y-8">
      {groups.map(group => <div key={group.kind ?? 'all'} data-catalog-group={group.kind} className={`grid ${getGridClass()} gap-x-3 gap-y-8`}>
        {group.items.map((artwork, index) => (
          <motion.div
            key={`${artwork.id}-${index}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.5,
              delay: (index % columns) * 0.05, // Sütun pozisyonuna göre kademeli gecikme
              ease: [0.25, 0.1, 0.25, 1.0], // Özel easing eğrisi
            }}
          >
            <CatalogCard
              artwork={artwork}
              onEnlarge={onEnlarge}
              onView={onView}
              onMouseEnter={onMouseEnter ? () => onMouseEnter(artwork) : undefined}
              onMouseLeave={onMouseLeave}
              onMouseMove={onMouseMove}
              onCopyWallet={onCopyWallet}
              copiedWallet={copiedWallet}
              catalogType={catalogType}
              useAspectRatio={useAspectRatio}
            />
          </motion.div>
        ))}
      </div>)}
    </div>
  );
}
